/**
 * SmartBasket — universal multi-retailer shopping basket + checkout.
 *
 * Route: /food-pulse/basket (alias /basket)
 *
 * User flow:
 *   1. Add products from Wishlist / Shopping List / Product pages
 *   2. Pick user location (persisted from LocationPicker)
 *   3. Tap "Optimise" → backend splits lines across the cheapest N stores
 *   4. Review sub-carts (Milk → Walmart · Eggs → Costco · Bread → Aldi)
 *   5. Tap "Checkout" → we return one handoff link per sub-cart
 *      (deep-link redirect to Instacart / DoorDash / partner site OR
 *      native FoodPulse-managed pickup if the retailer configured that)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingBag, Store, Truck, MapPin, ArrowRight, Plus, Trash2, Loader2,
  Sparkles, ExternalLink, DollarSign, TrendingDown, PackageOpen, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import LocationPicker, { getStoredLocation } from "@/components/LocationPicker";

const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";
const RED   = "#7B2A2E";

export default function SmartBasket() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [basket, setBasket] = useState(null);
  const [wishRows, setWishRows] = useState([]);
  const [location, setLocation] = useState(getStoredLocation());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Load or create a single active basket per user.
  const loadBasket = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await http.get("/delivery/baskets/mine");
      const items = r.data?.items || [];
      let b = items.find((x) => x.status === "draft" || x.status === "optimised");
      if (!b) {
        // Seed from wishlist so users don't stare at an empty page.
        const w = await http.get("/lists/wishlist");
        const wishItems = (w.data?.items || []).slice(0, 20);
        setWishRows(wishItems);
        const lines = wishItems.map((row) => ({
          food_item_slug: row.food_item_slug,
          name:           row.food_item_name || row.food_item_slug,
          quantity:       row.purchase_qty || row.quantity || 1,
          unit:           row.unit || "each",
        }));
        const created = await http.post("/delivery/baskets", {
          name:     "My basket",
          lines,
          user_lat: location?.latitude,
          user_lon: location?.longitude,
        });
        b = created.data;
      }
      setBasket(b);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load basket");
    }
    setLoading(false);
  }, [user, location]);

  useEffect(() => { loadBasket(); }, [loadBasket]);

  const runOptimise = async () => {
    if (!basket) return;
    setBusy(true);
    try {
      if (location?.latitude && location?.longitude &&
          (basket.user_lat !== location.latitude || basket.user_lon !== location.longitude)) {
        await http.patch(`/delivery/baskets/${basket.id}`, {
          user_lat: location.latitude, user_lon: location.longitude,
        });
      }
      const r = await http.post(`/delivery/baskets/${basket.id}/optimize`);
      setBasket(r.data);
      toast.success(`Split across ${r.data.store_count} store(s) · save $${r.data.total_saving}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Optimise failed");
    } finally { setBusy(false); }
  };

  const runCheckout = async () => {
    if (!basket) return;
    setBusy(true);
    try {
      const r = await http.post(`/delivery/baskets/${basket.id}/checkout`);
      setBasket({ ...basket, status: "checked-out", last_order: r.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(false); }
  };

  const removeLine = async (idx) => {
    const newLines = (basket.lines || []).filter((_, i) => i !== idx);
    const r = await http.patch(`/delivery/baskets/${basket.id}`, { lines: newLines });
    setBasket(r.data);
  };

  if (!user) return <SignInGate />;
  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
    <Loader2 className="w-6 h-6 animate-spin" style={{ color: GREEN }} />
  </div>;

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="smart-basket">
      <SeoHead section="food-pulse-basket" title="Smart basket · FoodPulse"
               description="Split your grocery basket across the cheapest stores with a single checkout." />

      <div className="max-w-4xl mx-auto px-4 pt-10 pb-8">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: AMBER }}>— Smart basket</div>
            <h1 className="font-display text-4xl">Shop the cheapest stores in one place</h1>
            <p className="opacity-70 text-sm mt-1 max-w-2xl">
              Add products, we split them across the retailers offering the best price near you, then hand off checkout to Instacart, DoorDash, Uber Eats, Store Pickup — or the retailer's own courier.
            </p>
          </div>
          <div className="min-w-[260px] max-w-[380px] flex-1">
            <LocationPicker value={location} onChange={setLocation} compact />
          </div>
        </div>

        {/* Basket lines --------------------------------- */}
        <div className="rounded-2xl border-2 bg-white p-4 mb-4" style={{ borderColor: "#E9D8B4" }} data-testid="basket-lines">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-lg">Your items ({basket?.lines?.length || 0})</div>
            <Link to="/food-pulse/my-lists?tab=wishlist"
                  className="text-xs font-semibold underline flex items-center gap-1"
                  data-testid="basket-add-from-wishlist">
              <Plus className="w-3 h-3" /> Add from wishlist
            </Link>
          </div>
          {(!basket?.lines || basket.lines.length === 0) ? (
            <EmptyBasket />
          ) : (
            <ul className="divide-y" style={{ borderColor: "#EED9AC" }}>
              {basket.lines.map((l, i) => (
                <li key={i} className="py-2 flex items-center gap-2 text-sm" data-testid={`basket-line-${i}`}>
                  <ShoppingBag className="w-4 h-4 opacity-40" />
                  <span className="flex-1 truncate">{l.name}</span>
                  <span className="text-xs opacity-60 font-mono">{l.quantity} {l.unit}</span>
                  <button onClick={() => removeLine(i)} className="p-1 hover:bg-red-50 rounded text-red-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Action bar ---------------------------------- */}
        <div className="flex gap-2 flex-wrap mb-6">
          <button onClick={runOptimise} disabled={busy || !basket?.lines?.length}
                  className="flex-1 rounded-full px-5 py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-40"
                  style={{ background: GREEN }}
                  data-testid="basket-optimise">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Optimise across stores
          </button>
        </div>

        {/* Sub-carts ---------------------------------- */}
        {basket?.sub_carts?.length > 0 && (
          <div className="mb-6" data-testid="basket-sub-carts">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display text-xl">Split across {basket.store_count} store{basket.store_count === 1 ? "" : "s"}</div>
              <div className="text-sm font-semibold" style={{ color: RED }}>
                <TrendingDown className="w-3 h-3 inline mr-1" />
                Est. saving ${basket.total_saving?.toFixed(2)}
              </div>
            </div>
            <div className="grid gap-3">
              {basket.sub_carts.map((sc, i) => (
                <SubCartCard key={i} sc={sc} basket={basket} handoff={basket.last_order?.handoffs?.[i]} />
              ))}
            </div>
            {basket.unresolved?.length > 0 && (
              <div className="mt-3 rounded-xl p-3 border-l-4 bg-amber-50" style={{ borderColor: AMBER }}>
                <div className="text-xs font-semibold flex items-center gap-1 mb-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Some items couldn't be placed
                </div>
                <ul className="text-xs opacity-80 list-disc list-inside">
                  {basket.unresolved.map((u, i) => (
                    <li key={i}><span className="font-semibold">{u.name}</span> — {u.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <button onClick={runCheckout} disabled={busy}
                    className="mt-4 w-full rounded-full px-5 py-3 font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-40"
                    style={{ background: RED }}
                    data-testid="basket-checkout">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageOpen className="w-4 h-4" />}
              Generate checkout links
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SubCartCard({ sc, handoff }) {
  const opt = handoff?.delivery_option;
  return (
    <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E9D8B4" }} data-testid={`basket-subcart-${sc.vendor_slug}`}>
      <div className="flex items-center gap-2 mb-2">
        <Store className="w-4 h-4" style={{ color: GREEN }} />
        <div className="font-semibold flex-1 min-w-0 truncate">{sc.vendor_name || sc.vendor_slug}</div>
        <div className="text-right">
          <div className="font-display text-lg font-bold" style={{ color: GREEN }}>${sc.subtotal?.toFixed(2)}</div>
          {sc.estimated_saving > 0 && (
            <div className="text-[11px]" style={{ color: RED }}>Save ${sc.estimated_saving.toFixed(2)}</div>
          )}
        </div>
      </div>
      <ul className="text-xs opacity-80 mb-2">
        {sc.lines.slice(0, 4).map((l, i) => (
          <li key={i} className="flex items-center justify-between gap-2 py-0.5">
            <span className="truncate">{l.quantity}× {l.name}</span>
            <span className="font-semibold flex-shrink-0">${l.line_total?.toFixed(2)}</span>
          </li>
        ))}
        {sc.lines.length > 4 && <li className="opacity-60">+ {sc.lines.length - 4} more…</li>}
      </ul>
      {opt && (
        <div className="mt-2 border-t pt-2 text-xs flex items-center gap-2 flex-wrap" style={{ borderColor: "#EED9AC" }}>
          <span className="rounded-full px-2 py-0.5 font-semibold" style={{ background: "#FFEDD5", color: GREEN }}>
            {opt.logo_emoji} {opt.provider_name}
          </span>
          {opt.delivery_fee != null && (
            <span className="opacity-80"><DollarSign className="w-3 h-3 inline" />{opt.delivery_fee?.toFixed(2)} fee</span>
          )}
          {opt.eta_minutes && <span className="opacity-80"><Truck className="w-3 h-3 inline mr-0.5" />~{opt.eta_minutes} min</span>}
          {opt.distance_km != null && <span className="opacity-80"><MapPin className="w-3 h-3 inline" />{opt.distance_km} km</span>}
          {opt.api_status === "not_configured" && (
            <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: "#FEF3C7", color: "#78350F" }}>
              handoff link
            </span>
          )}
        </div>
      )}
      {handoff?.redirect_url && (
        <a href={handoff.redirect_url} target="_blank" rel="noreferrer"
           className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full py-2 text-sm font-semibold text-white"
           style={{ background: GREEN }}
           data-testid={`basket-handoff-${sc.vendor_slug}`}>
          Complete on {opt?.provider_name || "partner"} <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

function EmptyBasket() {
  return (
    <div className="text-center py-10 opacity-70">
      <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-40" />
      <div className="text-sm">Your basket is empty.</div>
      <Link to="/food-pulse/my-lists?tab=wishlist" className="text-sm font-semibold underline">
        Pull items from your wishlist →
      </Link>
    </div>
  );
}

function SignInGate() {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
      <div className="max-w-md text-center">
        <ShoppingBag className="w-14 h-14 mx-auto mb-4" style={{ color: AMBER }} />
        <h1 className="font-display text-3xl mb-2">Sign in to build your smart basket</h1>
        <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }}>
          Sign in
        </Link>
      </div>
    </div>
  );
}
